import React, { useState, useEffect } from 'react'

import { CheckOutlined, DownloadOutlined, FileTextOutlined, PlayCircleOutlined, RollbackOutlined, WarningOutlined } from '@ant-design/icons'
import { Alert, Card, Descriptions, Modal, Select, Table, Tag, Space, Button, Typography, Spin, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useNavigate } from 'react-router-dom'
import ListPageHeader from '@shared/ListPageHeader'

import { operatorApiFetch } from '@services/operator/apiService'
import { pickingListService } from '@services/operator/pickingListService'
import { useFeatureEnabled } from '@contexts/operator/PlanContext'

import { formatDateTime } from '@/utils/dateTime'
import { useBackgroundJob } from '@/components/shared/BackgroundJobManager'


export default function PickingListShow() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const appPickingEnabled = useFeatureEnabled('picking_lists.app_picking')
  const [loading, setLoading] = useState(true)
  const [pickingList, setPickingList] = useState(null)
  const [reopenLoading, setReopenLoading] = useState(false)
  const [putBackModalOpen, setPutBackModalOpen] = useState(false)
  const [putBackModalLoading, setPutBackModalLoading] = useState(false)
  const [putBackSubmitting, setPutBackSubmitting] = useState(false)
  const [putBackItems, setPutBackItems] = useState([])
  const { addJob } = useBackgroundJob()
  const [exportingPickHistory, setExportingPickHistory] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await operatorApiFetch(`picking_lists/${id}.json`)
      const data = await response.json()
      if (data.success) {
        setPickingList(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch picking list:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!id) return
    fetchData()
  }, [id, operatorApiFetch])

  const handleDownloadPdf = async () => {
    try {
      const response = await operatorApiFetch(`picking_lists/${id}/generate_pdf`, { method: 'POST' })
      const data = await response.json()
      if (data.success) {
        addJob(data.data.job_id, 'common.button.download_pdf')
        message.success(t('background_job.workers.picking_lists.pdf_export.start'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      message.error(t('common.error.operation_failed'))
    }
  }

  const handleExportPickHistory = async () => {
    if (exportingPickHistory) return
    setExportingPickHistory(true)
    try {
      const data = await pickingListService.exportPickHistory(id)
      if (data.success) {
        addJob(data.data.job_id, 'shared.picking_lists.detail.picking_records.export.job_name')
        message.success(t('shared.picking_lists.detail.picking_records.export.success'))
      } else {
        message.error(data.message || t('shared.picking_lists.detail.picking_records.export.error'))
      }
    } catch (error) {
      console.error('Failed to export pick history:', error)
      message.error(t('shared.picking_lists.detail.picking_records.export.error'))
    } finally {
      setExportingPickHistory(false)
    }
  }

  const handleConfirm = () => {
    navigate(`/picking_lists/${id}/confirm`)
  }

  // Reopen：誤按 complete/terminate 後的救援（§6）
  // 紙本撿貨：reopen 後 ShelfAllocation 可能對 remaining>0 的部分重新生成，
  // 原 PDF 與新儲位不一致，先用 Modal 攔下提醒重新下載 PDF。
  const handleReopen = () => {
    Modal.confirm({
      title: t('shared.picking_lists.detail.reopen.confirm_title'),
      content: isPaperPicking
        ? t('shared.picking_lists.detail.reopen.confirm_description_paper')
        : t('shared.picking_lists.detail.reopen.confirm_description'),
      okText: t('shared.picking_lists.detail.reopen.button'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        setReopenLoading(true)
        try {
          const result = await pickingListService.reopen(id)
          if (result.success) {
            message.success(t('shared.picking_lists.detail.reopen.success'))
            fetchData()
          } else {
            const key = result.message && `errors.${result.message}`
            message.error(key ? t(key, t('common.error.operation_failed')) : t('common.error.operation_failed'))
          }
        } catch (error) {
          console.error('Failed to reopen picking list:', error)
          message.error(t('common.error.operation_failed'))
        } finally {
          setReopenLoading(false)
        }
      },
    })
  }

  // 開啟歸位 Tote Modal：查詢 tote 殘留 + 預設選擇來源儲位
  const handleOpenPutBackModal = async () => {
    setPutBackModalOpen(true)
    setPutBackModalLoading(true)
    setPutBackItems([])
    try {
      const result = await pickingListService.getToteItems(id)
      if (result.success) {
        const items = (result.data?.items || []).map((item) => ({
          inventory_id: item.inventory_id,
          product_sku: item.sku,
          product_name: item.product_name,
          quantity: item.quantity,
          source_shelves: item.source_shelves || [],
          shelf_id: item.source_shelves?.[0]?.shelf_id || null,
        }))
        setPutBackItems(items)
      } else {
        message.error(result.message || t('common.error.operation_failed'))
        setPutBackModalOpen(false)
      }
    } catch (error) {
      console.error('Failed to fetch tote items:', error)
      message.error(t('common.error.operation_failed'))
      setPutBackModalOpen(false)
    } finally {
      setPutBackModalLoading(false)
    }
  }

  const updatePutBackShelf = (inventoryId, shelfId) => {
    setPutBackItems((prev) =>
      prev.map((item) => (item.inventory_id === inventoryId ? { ...item, shelf_id: shelfId } : item))
    )
  }

  const handlePutBackConfirm = async () => {
    if (putBackItems.length === 0) return
    if (putBackItems.some((item) => !item.shelf_id)) {
      message.error(t('shared.picking_lists.detail.put_back_tote.missing_shelf'))
      return
    }

    setPutBackSubmitting(true)
    try {
      const payload = putBackItems.map((item) => ({
        inventory_id: item.inventory_id,
        shelf_id: item.shelf_id,
        quantity: item.quantity,
      }))
      const result = await pickingListService.putBackTote(id, payload)
      if (result.success) {
        message.success(t('shared.picking_lists.detail.put_back_tote.success'))
        Array.isArray(result.data?.warnings) && result.data.warnings.forEach((w) => {
          const key = w.message_key || (w.code && `errors.${w.code}`)
          message.warning(key ? t(key, w.code) : w.code)
        })
        setPutBackModalOpen(false)
        setPutBackItems([])
        fetchData()
      } else {
        const key = result.message && `errors.${result.message}`
        message.error(key ? t(key, result.message) : t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to put back tote:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setPutBackSubmitting(false)
    }
  }

  const isPaperPicking = pickingList?.picking_method === 'paper' || !appPickingEnabled
  const isFinished = pickingList?.status === 'done' || pickingList?.status === 'terminated'
  const hasToteResidue = (pickingList?.tote?.items?.length || 0) > 0
  // §6.2 新增 guard：done 且所有 PLI 已撿到應撿數量 → reopen noop，FE 隱藏按鈕
  // （後端 controller 同步擋下 PICKING_LIST_REOPEN_ALREADY_FULLY_PICKED）。terminate 不適用。
  const isFullyPicked =
    (pickingList?.picking_list_items?.length || 0) > 0 &&
    pickingList.picking_list_items.every((pli) => (pli.picked_quantity || 0) >= (pli.quantity || 0))
  const canReopen = isFinished && !(pickingList?.status === 'done' && isFullyPicked)

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!pickingList) {
    return <div>Picking List not found</div>
  }

  // 揀貨紀錄 Columns
  const pickingRecordColumns = [
    {
      title: t(`models.shelf.location`),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
    },
    {
      title: t(`models.product.sku`),
      dataIndex: 'product_sku',
      key: 'product_sku',
    },
    {
      title: t(`models.product.name`),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t(`models.picking_record.type`),
      dataIndex: 'quantity',
      key: 'type',
      render: (qty) => (
        <Tag color={qty > 0 ? 'blue' : 'orange'}>
          {qty > 0 ? t('models.picking_record.pick') : t('models.picking_record.put_back')}
        </Tag>
      ),
    },
    {
      title: t(`models.picking_record.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (qty) => Math.abs(qty),
    },
    {
      title: t(`models.operator.name`),
      dataIndex: 'operator_name',
      key: 'operator_name',
    },
    {
      title: t(`common.created_at`),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => formatDateTime(text),
    },
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'blue'
      case 'processing': return 'orange'
      case 'done': return 'green'
      case 'terminated': return 'volcano'
      default: return 'default'
    }
  }

  return (
    <div>
      <ListPageHeader
        items={[
          { label: t('operator.menu.picking.title') },
          { label: t('operator.menu.picking_lists.list'), path: '/picking_lists' },
          { label: pickingList.display_name },
        ]}
        actions={
          <Space>
            {pickingList.status === 'processing' && isPaperPicking && (
              <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirm}>
                {t('shared.picking_lists.detail.verify_picking_button')}
              </Button>
            )}

            <Button icon={<FileTextOutlined />} onClick={handleDownloadPdf}>
              {t('shared.picking_lists.detail.actions.download_current_picking_list')}
            </Button>
          </Space>
        }
      />

      {/* 攔截訂單警示 */}
      {pickingList.held_orders?.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message={t('shared.picking_lists.detail.held_orders.title', { count: pickingList.held_orders.length })}
          description={
            <div>
              {pickingList.held_orders.map((o) => (
                <div key={o.id}>
                  <Link to={`/orders/${o.id}`}>{o.name}</Link>
                  {' — '}
                  {o.hold_reasons.map((r) => (
                    <Tag key={r} color="orange" style={{ marginLeft: 4 }}>
                      {t(`enums.order_hold.reason.${r}`, r)}
                    </Tag>
                  ))}
                </div>
              ))}
              {pickingList.picking_wave && (
                <div style={{ marginTop: 8 }}>
                  <Link
                    to={`/picking_waves/${pickingList.picking_wave.id}#fulfillments`}
                    state={{ highlightOrderIds: pickingList.held_orders.map((o) => o.id) }}
                  >
                    {t('shared.picking_lists.detail.held_orders.go_to_wave_to_return')}
                  </Link>
                </div>
              )}
            </div>
          }
        />
      )}

      {/* 已結束但 tote 仍有殘留：提示可 Reopen 繼續揀貨或 Put Back 歸位 (§7.1) */}
      {isFinished && hasToteResidue && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message={t('shared.picking_lists.detail.finished_tote_residual.title')}
          description={t('shared.picking_lists.detail.finished_tote_residual.description')}
        />
      )}

      {/* 基本資訊 */}
      <Card
        title={
          <Space>
            {pickingList.purpose === 'work_order' ? t('work_orders.picking_list.title') : t(`shared.picking_lists.detail.title`)}
            <Tag color={getStatusColor(pickingList.status)}>
              {t(`enums.picking_list.status.${pickingList.status}`)}
            </Tag>
            <Tag color={pickingList.picking_method === 'app' ? 'blue' : 'orange'}>
              {t(`shared.picking_lists.generate.options.${pickingList.picking_method || 'app'}`)}
            </Tag>
            <span style={{ fontWeight: 'normal', fontSize: '14px', color: '#999' }}>
              {pickingList.display_name}
            </span>
          </Space>
        }
        style={{ marginBottom: '16px' }}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t(`models.picking_list.name`)}>
            {pickingList.display_name}
          </Descriptions.Item>
          <Descriptions.Item label={t(`shared.picking_lists.detail.wave.label`)}>
            {pickingList.picking_wave ? (
              <Link to={`/picking_waves/${pickingList.picking_wave.id}`}>
                {pickingList.picking_wave.name}
              </Link>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.picking_list.items_count`)}>
            {pickingList.items_count}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.picking_list.status`)}>
            <Tag color={getStatusColor(pickingList.status)}>
              {t(`enums.picking_list.status.${pickingList.status}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t(`shared.picking_lists.generate.options.picking_method`)}>
            <Tag color={pickingList.picking_method === 'app' ? 'blue' : 'orange'}>
              {t(`shared.picking_lists.generate.options.${pickingList.picking_method || 'app'}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t(`common.created_at`)}>
            {formatDateTime(pickingList.created_at)}
          </Descriptions.Item>
        </Descriptions>

        {/* 波次資訊 — 同批次揀貨單 */}
        {pickingList.picking_wave && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Typography.Text type="secondary">
                {t('shared.picking_lists.detail.wave.siblings', {
                  wave: pickingList.picking_wave.name,
                  count: (pickingList.sibling_picking_lists?.length || 0) + 1
                })}
              </Typography.Text>
            </div>
            {pickingList.sibling_picking_lists?.length > 0 && (
              <Space wrap>
                {pickingList.sibling_picking_lists.map(sibling => (
                  <Link key={sibling.id} to={`/picking_lists/${sibling.id}`}>
                    <Tag color={getStatusColor(sibling.status)} style={{ cursor: 'pointer' }}>
                      {sibling.display_name} - {t(`enums.picking_list.status.${sibling.status}`)}
                    </Tag>
                  </Link>
                ))}
              </Space>
            )}
          </div>
        )}
      </Card>

      {/* 揀貨項目 */}
      {pickingList.picking_list_items?.length > 0 && (
        <Card
          title={t('shared.picking_lists.detail.picking_list_items.title')}
          style={{ marginBottom: '16px' }}
        >
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('shared.picking_lists.detail.picking_list_items.subtitle')}
          </Typography.Text>
          <Table
            columns={[
              {
                title: t('models.product.sku'),
                dataIndex: 'sku',
                key: 'sku',
                width: 150,
              },
              {
                title: t('models.product.name'),
                dataIndex: 'product_name',
                key: 'product_name',
              },
              {
                title: t('models.product.barcode'),
                dataIndex: 'barcode',
                key: 'barcode',
                width: 150,
              },
              {
                title: t('models.picking_list_item.quantity'),
                dataIndex: 'quantity',
                key: 'quantity',
                width: 100,
                sorter: (a, b) => a.quantity - b.quantity,
              },
              {
                title: t('models.picking_list_item.picked_quantity'),
                dataIndex: 'picked_quantity',
                key: 'picked_quantity',
                width: 100,
              },
              {
                title: t('models.picking_list_item.is_done'),
                dataIndex: 'is_done',
                key: 'is_done',
                width: 80,
                render: (done) => (
                  <Tag color={done ? 'green' : 'default'}>
                    {done ? t('common.yes') : t('common.no')}
                  </Tag>
                ),
              },
            ]}
            dataSource={pickingList.picking_list_items}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Tote 內容物(殘留庫存) */}
      {pickingList.tote && pickingList.tote.items?.length > 0 && (
        <Card
          title={
            <Space>
              {t('shared.picking_lists.detail.tote.title')}
              <Tag color="blue">{pickingList.tote.location}</Tag>
              <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                {t('shared.picking_lists.detail.tote.total_quantity', { count: pickingList.tote.total_quantity })}
              </Typography.Text>
            </Space>
          }
          extra={isFinished && (
            <Space>
              {canReopen && (
                <Button icon={<PlayCircleOutlined />} loading={reopenLoading} onClick={handleReopen}>
                  {t('shared.picking_lists.detail.reopen.button')}
                </Button>
              )}
              <Button
                type="primary"
                icon={<RollbackOutlined />}
                onClick={handleOpenPutBackModal}
              >
                {t('shared.picking_lists.detail.put_back_tote.button')}
              </Button>
            </Space>
          )}
          style={{ marginBottom: '16px' }}
        >
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('shared.picking_lists.detail.tote.subtitle')}
          </Typography.Text>
          <Table
            columns={[
              {
                title: t('models.product.sku'),
                dataIndex: 'sku',
                key: 'sku',
                width: 180,
              },
              {
                title: t('models.product.name'),
                dataIndex: 'product_name',
                key: 'product_name',
              },
              {
                title: t('models.picking_list_item.quantity'),
                dataIndex: 'quantity',
                key: 'quantity',
                width: 120,
                align: 'right',
              },
            ]}
            dataSource={pickingList.tote.items}
            rowKey="inventory_id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* 歸位 Tote Modal */}
      <Modal
        title={t('shared.picking_lists.detail.put_back_tote.modal_title')}
        open={putBackModalOpen}
        onCancel={() => { setPutBackModalOpen(false); setPutBackItems([]) }}
        onOk={handlePutBackConfirm}
        confirmLoading={putBackSubmitting}
        okText={t('shared.picking_lists.detail.put_back_tote.confirm')}
        cancelText={t('common.button.cancel')}
        okButtonProps={{ disabled: putBackModalLoading || putBackItems.length === 0 }}
        width={760}
      >
        {putBackModalLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              message={t('shared.picking_lists.detail.put_back_tote.warning')}
              description={t('shared.picking_lists.detail.put_back_tote.description')}
              style={{ marginBottom: 16 }}
            />
            <Table
              size="small"
              pagination={false}
              dataSource={putBackItems}
              rowKey="inventory_id"
              columns={[
                {
                  title: t('models.product.sku'),
                  key: 'sku',
                  width: 140,
                  render: (_, record) => (
                    <div>
                      <div>{record.product_sku || '-'}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{record.product_name}</div>
                    </div>
                  ),
                },
                {
                  title: t('models.picking_list_item.quantity'),
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 80,
                  align: 'right',
                },
                {
                  title: t('shared.picking_lists.detail.put_back_tote.target_shelf'),
                  key: 'target_shelf',
                  render: (_, record) => {
                    const options = (record.source_shelves || []).map((s) => ({
                      value: s.shelf_id,
                      label: `${s.location} (${t('shared.picking_lists.detail.put_back_tote.picked_from')})`,
                    }))
                    return (
                      <Select
                        style={{ width: '100%' }}
                        value={record.shelf_id}
                        onChange={(val) => updatePutBackShelf(record.inventory_id, val)}
                        options={options}
                        placeholder={t('shared.picking_lists.detail.put_back_tote.select_shelf')}
                      />
                    )
                  },
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* 揀貨紀錄 */}
      <Card
        title={t(`shared.picking_lists.detail.picking_records.title`)}
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportPickHistory}
            loading={exportingPickHistory}
            disabled={(pickingList.pick_history || []).length === 0}
          >
            {t('shared.picking_lists.detail.picking_records.export.button')}
          </Button>
        }
      >
        <Table
          columns={pickingRecordColumns}
          dataSource={pickingList.pick_history || []}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="small"
        />
      </Card>
    </div>
  )
}
